import { useState } from "react";
import { Navigate, Link } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { PasskeyLoginButton } from "../../components/PasskeyLoginButton";

export function LoginPage() {
  const { isAuthenticated, isLoading, loginWithGoogle, loginWithMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function handleGoogleLogin() {
    try {
      await loginWithGoogle();
    } catch {
      setError("Failed to start Google login");
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await loginWithMagicLink(email);
      setMagicLinkSent(true);
    } catch {
      setError("Failed to send login link");
    } finally {
      setSending(false);
    }
  }

  if (magicLinkSent) {
    return (
      <div className="mx-auto max-w-sm py-16 text-center">
        <h1 className="mb-4 text-2xl font-bold">Check your email</h1>
        <p className="mb-6 text-gray-600">
          We sent a login link to <span className="font-medium">{email}</span>.
          Click the link in the email to log in.
        </p>
        <button
          onClick={() => { setMagicLinkSent(false); setEmail(""); }}
          className="text-sm font-medium text-gray-900 underline hover:no-underline"
        >
          Try a different email
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm py-16">
      <h1 className="mb-8 text-center text-2xl font-bold">Log in to Abejón</h1>

      <div className="space-y-4">
        {/* Google OAuth */}
        <button
          onClick={handleGoogleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 hover:bg-gray-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        {/* Passkey */}
        <PasskeyLoginButton
          onSuccess={() => window.location.replace("/auth/callback")}
          onError={(msg) => setError(msg)}
        />

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm text-gray-500">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Magic Link */}
        <form onSubmit={handleMagicLink} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
          <button
            type="submit"
            disabled={sending}
            className="w-full rounded-lg bg-gray-900 px-4 py-3 font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {sending ? "Sending..." : "Send login link"}
          </button>
        </form>

        {error && (
          <p className="text-center text-sm text-red-600">{error}</p>
        )}
      </div>

      <p className="mt-6 text-center text-sm text-gray-500">
        Booked as a guest?{" "}
        <Link to="/bookings/lookup" className="font-medium text-gray-900 hover:underline">
          Look up your booking
        </Link>
      </p>
    </div>
  );
}
